import React, { useState, useEffect } from 'react'
import {
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Avatar,
  IconButton,
  Grid
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Payments as PaymentsIcon,
  AccountBalanceWallet as WalletIcon
} from '@mui/icons-material'
import { payrollApi } from '../../../api/payrollApi'
import toast from 'react-hot-toast'
import { format } from 'date-fns'
import { useNavigate, useParams } from 'react-router-dom'
import TableSkeleton from '../../../components/common/Skeleton/TableSkeleton'

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  }).format(amount || 0)
}

const getMonthName = (monthNumber) => {
  const date = new Date()
  date.setMonth(monthNumber - 1)
  return date.toLocaleString('default', { month: 'long' })
}

const PayrollHistoryPage = () => {
  const { employeeId } = useParams()
  const navigate = useNavigate()
  
  const [historyData, setHistoryData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (employeeId) {
      fetchHistory()
    }
  }, [employeeId])

  const fetchHistory = async () => {
    setLoading(true)
    try {
      const response = await payrollApi.getEmployeePayrollHistory({ employee_id: employeeId })
      setHistoryData(response.data)
    } catch (error) {
      console.error('Error fetching payroll history', error)
      toast.error('Failed to fetch payroll history')
      navigate('/org/payroll')
    } finally {
      setLoading(false)
    }
  }

  const handleViewPayslip = (payrollId) => {
    navigate(`/org/payroll/payslip/${payrollId}`)
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        
        {/* Header */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/org/payroll')}
            sx={{ mr: 2, color: 'text.secondary' }}
          >
            Back
          </Button>
          <Typography variant="h4" fontWeight="bold">
            Payroll History
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <CircularProgress />
          </Box>
        ) : historyData ? (
          <>
            {/* Employee Banner */}
            <Box sx={{ 
              display: 'flex', alignItems: 'center', gap: 3, mb: 4, p: 3, 
              bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.08)' : '#f0f7ff', 
              borderRadius: 2, 
              border: '1px solid',
              borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.2)' : '#bbdefb'
            }}>
              <Avatar sx={{ width: 72, height: 72, bgcolor: 'primary.main', fontSize: 32 }}>
                {historyData.employee_name?.charAt(0) || <PersonIcon fontSize="large" />}
              </Avatar>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="primary.main">{historyData.employee_name}</Typography>
                <Typography variant="body1" color="primary.main" sx={{ mt: 0.5, fontWeight: 'medium' }}>
                  {historyData.department}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Employee ID: {historyData.employee_code}
                </Typography>
              </Box>
            </Box>

            {/* Table */}
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
              Records ({historyData.total_records})
            </Typography>
            <Paper variant="outlined" sx={{ borderRadius: 2 }}>
              <TableContainer sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 650 }}>
                  <TableHead sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#fafafa' }}>
                    <TableRow>
                      <TableCell>Payroll Period</TableCell>
                      <TableCell>Base Salary</TableCell>
                      <TableCell>Deductions</TableCell>
                      <TableCell>Net Salary</TableCell>
                      <TableCell>Generated On</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historyData.records && historyData.records.length > 0 ? (
                      historyData.records.map((record) => (
                        <TableRow key={record.id} hover>
                          <TableCell>
                            <Typography fontWeight="bold" color="text.primary">
                              {getMonthName(record.month)} {record.year}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {formatCurrency(record.base_salary)}
                          </TableCell>
                          <TableCell>
                            {record.lop_deduction > 0 ? (
                              <Typography color="error.main" fontWeight="medium">
                                {formatCurrency(record.lop_deduction)}
                              </Typography>
                            ) : (
                              <Typography color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" color="primary.main">
                              {formatCurrency(record.net_salary)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {format(new Date(record.generated_at), 'dd MMM yyyy')}
                            </Typography>
                            <Typography variant="caption" color="text.disabled">
                              {format(new Date(record.generated_at), 'HH:mm')}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="small" 
                              color="primary" 
                              onClick={() => handleViewPayslip(record.id)}
                              title="View Payslip"
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                          <Typography variant="body1" color="textSecondary">
                            No payroll records found for this employee.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            <Typography color="error">Failed to load history data.</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  )
}

export default PayrollHistoryPage
